import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import DocumentLinker from '../../components/forms/DocumentLinker';
import {
  TaskCategoriesClient,
  TaskCategoryDto,
  TaskPrioritiesClient,
  TaskPriorityDto,
} from '../../app/services/api/tasksClient';
import type { StepType, EvidenceType } from '../../app/services/api/tasksClient';

const categoriesClient = TaskCategoriesClient;
const prioritiesClient = TaskPrioritiesClient;

interface AdhocTaskStepDto {
  id: number;
  order: number;
  stepType: StepType;
  instruction: string;
  linkedDocumentIds: number[];
  linkedDocumentName?: string | null;
  linkedUrl?: string | null;
  linkedUrlLabel?: string | null;
  evidenceRequired: boolean;
  evidenceType?: EvidenceType | null;
}

const CreateAdhocTask: React.FC = () => {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermissionTo, franchises, stores } = useContext(UserContext);

  const canCreate = hasPermissionTo ? hasPermissionTo(['AdvancedTasksCreate']) : false;

  // Reference data
  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);

  // Task Details Form
  const [formValues, setFormValues] = useState<{
    title: string;
    description: string;
    taskCategoryId: number | '';
    taskPriorityId: number | '';
    dueDate: string;
    dueTime: string;
    expectedDurationMins: number | '';
    requiresApproval: boolean;
  }>({
    title: '',
    description: '',
    taskCategoryId: '',
    taskPriorityId: '',
    dueDate: '',
    dueTime: '',
    expectedDurationMins: '',
    requiresApproval: false,
  });

  // Assignment
  const [assignmentType, setAssignmentType] = useState<'LOCATION' | 'COLLEAGUE'>('LOCATION');
  const [selectedFranchiseId, setSelectedFranchiseId] = useState<number | ''>('');
  const [selectedStoreNumbers, setSelectedStoreNumbers] = useState<string[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<number | ''>('');
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');

  // Approver (optional)
  const [approverType, setApproverType] = useState<'persona' | 'user' | ''>('');
  const [approverPersonaId, setApproverPersonaId] = useState<number | ''>('');
  const [approverUserId, setApproverUserId] = useState<number | ''>('');

  // Steps
  const [steps, setSteps] = useState<AdhocTaskStepDto[]>([]);
  const [isStepModalOpen, setIsStepModalOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<AdhocTaskStepDto | null>(null);
  const [stepFormValues, setStepFormValues] = useState<{
    stepType: StepType;
    instruction: string;
    linkedDocumentIds: number[];
    linkedUrl: string;
    linkedUrlLabel: string;
    evidenceRequired: boolean;
    evidenceType: EvidenceType | '';
  }>({
    stepType: 'text',
    instruction: '',
    linkedDocumentIds: [],
    linkedUrl: '',
    linkedUrlLabel: '',
    evidenceRequired: false,
    evidenceType: '',
  });

  // Delete step dialog
  const [isDeleteStepDialogOpen, setIsDeleteStepDialogOpen] = useState(false);
  const [stepIdPendingDelete, setStepIdPendingDelete] = useState<number | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Mock data
  const fakePersonas = [
    { id: 1, name: 'Store Manager' },
    { id: 2, name: 'Assistant Manager' },
    { id: 3, name: 'Team Member' },
  ];

  const fakeUsers = [
    { id: 1001, name: 'Alex Smith' },
    { id: 1002, name: 'Jamie Lee' },
    { id: 1003, name: 'Taylor Brown' },
  ];

  const evidenceTypeOptions: { value: EvidenceType; label: string }[] = [
    { value: 'photo', label: t('adhoc.evidenceTypePhoto', 'Photo') },
    { value: 'signature', label: t('adhoc.evidenceTypeSignature', 'Signature') },
    { value: 'text', label: t('adhoc.evidenceTypeText', 'Text Note') },
    { value: 'document', label: t('adhoc.evidenceTypeDocument', 'Document Upload') },
    { value: 'checkbox', label: t('adhoc.evidenceTypeCheckbox', 'Checkbox Confirmation') },
    { value: 'numeric', label: t('adhoc.evidenceTypeNumeric', 'Numeric Value') },
    { value: 'financial', label: t('adhoc.evidenceTypeFinancial', 'Financial Value') },
  ];

  useEffect(() => {
    categoriesClient.getCategories().then(setCategories).catch(() => setCategories([]));
    prioritiesClient.getPriorities().then(setPriorities).catch(() => setPriorities([]));
  }, []);

  // Filter stores based on selected franchise
  const filteredStores = selectedFranchiseId
    ? stores.filter(s => s.franchiseId === selectedFranchiseId)
    : stores;

  if (!canCreate) {
    return (
      <PageArea>
        <PageTitle>{t('adhoc.title', 'Create Ad-hoc Task')}</PageTitle>
        <Pill text={t('adhoc.noPermission', 'You do not have permission to create tasks.')}
          pallet={theme.palette.error} />
      </PageArea>
    );
  }

  const validate = () => {
    const e: { [key: string]: string } = {};
    if (!formValues.title.trim()) e.title = t('adhoc.errorRequired', 'Required');
    if (!formValues.taskPriorityId) e.taskPriorityId = t('adhoc.errorRequired', 'Required');
    if (!formValues.dueDate) e.dueDate = t('adhoc.errorRequired', 'Required');
    if (!selectedFranchiseId) e.franchiseId = t('adhoc.errorRequired', 'Required');

    if (assignmentType === 'LOCATION') {
      if (selectedStoreNumbers.length === 0) e.storeNumbers = t('adhoc.errorSelectAtLeastOneStore', 'Select at least one store');
    } else {
      if (!selectedPersonaId && !selectedUserId) e.assignment = t('adhoc.errorSelectRoleOrUser', 'Select a role or user');
    }

    if (steps.length === 0) {
      e.steps = t('adhoc.errorAtLeastOneStep', 'At least one step is required');
    }

    if (formValues.requiresApproval && !approverPersonaId && !approverUserId) {
      e.approver = t('adhoc.errorApproverRequired', 'Approver is required when approval is enabled');
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Build the ad-hoc task DTO
    const adhocTask = {
      title: formValues.title,
      description: formValues.description,
      taskCategoryId: formValues.taskCategoryId === '' ? null : Number(formValues.taskCategoryId),
      taskPriorityId: Number(formValues.taskPriorityId),
      dueDateUtc: formValues.dueDate + (formValues.dueTime ? `T${formValues.dueTime}:00Z` : 'T23:59:59Z'),
      expectedDurationMins: formValues.expectedDurationMins === '' ? null : Number(formValues.expectedDurationMins),
      requiresApproval: formValues.requiresApproval,
      approverPersonaId: approverPersonaId === '' ? null : Number(approverPersonaId),
      approverUserId: approverUserId === '' ? null : Number(approverUserId),
      assignment: {
        franchiseId: Number(selectedFranchiseId),
        scopeType: assignmentType,
        storeNumbers: assignmentType === 'LOCATION' ? selectedStoreNumbers : null,
        personaId: assignmentType === 'COLLEAGUE' && selectedPersonaId ? Number(selectedPersonaId) : null,
        userId: assignmentType === 'COLLEAGUE' && selectedUserId ? Number(selectedUserId) : null,
      },
      steps: steps.map((s, idx) => ({
        ...s,
        order: idx + 1,
      })),
    };

    // TODO: Call API to create ad-hoc task
    console.log('Creating ad-hoc task:', adhocTask);

    navigate('/tasks');
  };

  const handleCancel = () => {
    navigate('/tasks');
  };

  // Step management
  const handleOpenAddStep = () => {
    setEditingStep(null);
    setStepFormValues({
      stepType: 'text',
      instruction: '',
      linkedDocumentIds: [],
      linkedUrl: '',
      linkedUrlLabel: '',
      evidenceRequired: false,
      evidenceType: '',
    });
    setIsStepModalOpen(true);
  };

  const handleOpenEditStep = (step: AdhocTaskStepDto) => {
    setEditingStep(step);
    setStepFormValues({
      stepType: step.stepType,
      instruction: step.instruction,
      linkedDocumentIds: step.linkedDocumentIds ?? [],
      linkedUrl: step.linkedUrl ?? '',
      linkedUrlLabel: step.linkedUrlLabel ?? '',
      evidenceRequired: step.evidenceRequired,
      evidenceType: step.evidenceType ?? '',
    });
    setIsStepModalOpen(true);
  };

  const handleSaveStep = () => {
    if (stepFormValues.stepType === 'text' && !stepFormValues.instruction.trim()) {
      return; // instruction required for text steps
    }
    if (stepFormValues.stepType === 'document' && stepFormValues.linkedDocumentIds.length === 0) {
      return; // document required for document steps
    }
    if (stepFormValues.stepType === 'url' && !stepFormValues.linkedUrl.trim()) {
      return; // url required for url steps
    }

    const stepData: AdhocTaskStepDto = {
      id: editingStep?.id ?? Date.now(),
      order: editingStep?.order ?? steps.length + 1,
      stepType: stepFormValues.stepType,
      instruction: stepFormValues.instruction,
      linkedDocumentIds: stepFormValues.linkedDocumentIds,
      linkedUrl: stepFormValues.linkedUrl || null,
      linkedUrlLabel: stepFormValues.linkedUrlLabel || null,
      evidenceRequired: stepFormValues.evidenceRequired,
      evidenceType: stepFormValues.evidenceRequired && stepFormValues.evidenceType
        ? stepFormValues.evidenceType
        : null,
    };

    if (editingStep) {
      setSteps(prev => prev.map(s => (s.id === editingStep.id ? stepData : s)));
    } else {
      setSteps(prev => [...prev, stepData]);
    }

    setIsStepModalOpen(false);
  };

  const handleRequestDeleteStep = (stepId: number) => {
    setStepIdPendingDelete(stepId);
    setIsDeleteStepDialogOpen(true);
  };

  const handleConfirmDeleteStep = () => {
    if (stepIdPendingDelete !== null) {
      setSteps(prev => prev.filter(s => s.id !== stepIdPendingDelete));
    }
    setIsDeleteStepDialogOpen(false);
    setStepIdPendingDelete(null);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(steps);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setSteps(reordered.map((s, i) => ({ ...s, order: i + 1 })));
  };

  return (
    <PageArea>
      <Grid
        container
        justifyContent='space-between'
        alignItems='center'
      >
        <Grid item>
          <PageTitle>{t('adhoc.title', 'Create Ad-hoc Task')}</PageTitle>
        </Grid>
        <Grid item>
          <Grid
            container
            spacing={2}
          >
            <Grid item>
              <Button
                variant='secondary'
                onClick={handleCancel}
              >
                {t('adhoc.cancel', 'Cancel')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='primary'
                onClick={handleSave}
              >
                {t('adhoc.createTask', 'Create Task')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Section style={{ marginTop: '8px', padding: 8, paddingTop: 2 }}>
        <h3>{t('adhoc.taskDetails', 'Task Details')}</h3>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <TextField
              label={t('adhoc.fieldTaskTitle', 'Task Title')}
              fullWidth
              required
              value={formValues.title}
              onChange={e => setFormValues(prev => ({ ...prev, title: e.target.value }))}
              error={!!errors.title}
              helperText={errors.title}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <TextField
              select
              label={t('adhoc.fieldCategory', 'Category')}
              fullWidth
              value={formValues.taskCategoryId}
              onChange={e => setFormValues(prev => ({ ...prev, taskCategoryId: e.target.value === '' ? '' : Number(e.target.value) }))}
            >
              <MenuItem value=''>{t('adhoc.placeholderNone', '-- None --')}</MenuItem>
              {categories.map(c => (
                <MenuItem
                  key={c.id}
                  value={c.id}
                >
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <TextField
              label={t('adhoc.fieldDescription', 'Description')}
              fullWidth
              multiline
              rows={3}
              value={formValues.description}
              onChange={e => setFormValues(prev => ({ ...prev, description: e.target.value }))}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <TextField
              select
              label={t('adhoc.fieldPriority', 'Priority')}
              fullWidth
              required
              value={formValues.taskPriorityId}
              onChange={e => setFormValues(prev => ({ ...prev, taskPriorityId: e.target.value === '' ? '' : Number(e.target.value) }))}
              error={!!errors.taskPriorityId}
              helperText={errors.taskPriorityId}
            >
              <MenuItem value=''>{t('adhoc.placeholderSelect', '-- Select --')}</MenuItem>
              {priorities.map(p => (
                <MenuItem
                  key={p.id}
                  value={p.id}
                >
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <TextField
              label={t('adhoc.fieldDueDate', 'Due Date')}
              type='date'
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={formValues.dueDate}
              onChange={e => setFormValues(prev => ({ ...prev, dueDate: e.target.value }))}
              error={!!errors.dueDate}
              helperText={errors.dueDate}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <TextField
              label={t('adhoc.fieldDueTime', 'Due Time (optional)')}
              type='time'
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formValues.dueTime}
              onChange={e => setFormValues(prev => ({ ...prev, dueTime: e.target.value }))}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <TextField
              label={t('adhoc.fieldExpectedDuration', 'Expected Duration (mins)')}
              type='number'
              fullWidth
              value={formValues.expectedDurationMins}
              onChange={e => setFormValues(prev => ({ ...prev, expectedDurationMins: e.target.value === '' ? '' : Number(e.target.value) }))}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.requiresApproval}
                  onChange={e => setFormValues(prev => ({ ...prev, requiresApproval: e.target.checked }))}
                />
              }
              label={t('adhoc.fieldRequiresApproval', 'Requires approval upon completion')}
            />
          </Grid>
        </Grid>
        {/* Steps Section */}
        <Section style={{ marginTop: '8px', padding: 8, paddingTop: 2, boxShadow: 'none' }}>
          <Grid
            container
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 2 }}
          >
            <Grid item>
              <h4>{t('adhoc.stepsHeading', 'Steps')}</h4>
            </Grid>
            <Grid item>
              <Button
                size='md'
                variant='primary'
                onClick={handleOpenAddStep}
              >
                {t('adhoc.addStep', 'Add Step')}
              </Button>
            </Grid>
          </Grid>

          {errors.steps && (
            <Pill text={errors.steps}
              pallet={theme.palette.error} />
          )}

          {steps.length > 0 && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId='steps-list'>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {steps.map((step, index) => (
                      <Draggable
                        key={step.id}
                        draggableId={String(step.id)}
                        index={index}
                      >
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            role="button"
                            tabIndex={0}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 12px',
                              marginBottom: 8,
                              border: '1px solid #ddd',
                              borderRadius: 4,
                              backgroundColor: '#fff',
                              cursor: 'pointer',
                              ...dragProvided.draggableProps.style,
                            }}
                            onClick={() => handleOpenEditStep(step)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleOpenEditStep(step);
                              }
                            }}
                          >
                            <div
                              {...dragProvided.dragHandleProps}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginRight: 8,
                                color: '#888',
                                cursor: 'grab',
                              }}
                            >
                              <DragIndicatorIcon fontSize='small' />
                            </div>
                            <div style={{ flex: 1 }}>
                              <strong>{index + 1}.</strong>{' '}
                              {step.stepType === 'document' ? (
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                >
                                  <DescriptionIcon
                                    fontSize="small"
                                    color="primary"
                                  />
                                  <Typography
                                    component="span"
                                    variant="textSM"
                                    color="primary"
                                  >
                                    {step.linkedDocumentName || t('adhoc.stepDocumentFallback', 'Document')}
                                  </Typography>
                                </Box>
                              ) : step.stepType === 'url' ? (
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                >
                                  <LinkIcon
                                    fontSize="small"
                                    color="secondary"
                                  />
                                  <Typography
                                    component="span"
                                    variant="textSM"
                                    color="secondary"
                                  >
                                    {step.linkedUrlLabel || step.linkedUrl}
                                  </Typography>
                                </Box>
                              ) : (
                                step.instruction
                              )}
                              {step.evidenceRequired && (
                                <div style={{ fontSize: 12, color: '#666' }}>
                                  {t('adhoc.stepEvidenceLabel', 'Evidence: {{label}}', { label: evidenceTypeOptions.find(o => o.value === step.evidenceType)?.label || t('adhoc.stepEvidenceRequiredFallback', 'Required') })}
                                </div>
                              )}
                            </div>
                            <div
                              role="button"
                              tabIndex={0}
                              aria-label={t('adhoc.deleteStepAriaLabel', 'Delete step {{number}}', { number: index + 1 })}
                              onClick={e => {
                                e.stopPropagation();
                                handleRequestDeleteStep(step.id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRequestDeleteStep(step.id);
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 8,
                                color: '#c00',
                                cursor: 'pointer',
                              }}
                            >
                              <DeleteOutlineIcon fontSize='small' />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {steps.length === 0 && (
            <Typography
              color='text.secondary'
              sx={{ py: 2, textAlign: 'center' }}
            >
              {t('adhoc.noStepsAdded', 'No steps added yet. Click "Add Step" to create task steps.')}
            </Typography>
          )}
        </Section>

      </Section>

      <Section style={{ marginTop: '8px', padding: 8, paddingTop: 2 }}>
        <h3>{t('adhoc.assignmentHeading', 'Assignment')}</h3>
        <Grid
          container
          spacing={3}
        >
          {/* Franchise selection first */}
          <Grid
            item
            xs={12}
            md={6}
          >
            <TextField
              select
              label={t('adhoc.fieldFranchise', 'Franchise')}
              fullWidth
              required
              value={selectedFranchiseId}
              onChange={e => {
                setSelectedFranchiseId(e.target.value === '' ? '' : Number(e.target.value));
                setSelectedStoreNumbers([]); // Clear stores when franchise changes
              }}
              error={!!errors.franchiseId}
              helperText={errors.franchiseId}
            >
              <MenuItem value=''>{t('adhoc.placeholderSelectFranchise', '-- Select Franchise --')}</MenuItem>
              {franchises.map(f => (
                <MenuItem
                  key={f.franchiseId}
                  value={f.franchiseId}
                >
                  {f.franchiseName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Assignment type */}
          <Grid
            item
            xs={12}
          >
            <FormControl component='fieldset'>
              <FormLabel component='legend'>{t('adhoc.assignmentTypeQuestion', 'How should this task be assigned?')}</FormLabel>
              <RadioGroup
                row
                value={assignmentType}
                onChange={e => setAssignmentType(e.target.value as 'LOCATION' | 'COLLEAGUE')}
              >
                <FormControlLabel
                  value='LOCATION'
                  control={<Radio />}
                  label={t('adhoc.assignmentByLocation', 'By Location - One task per store, assigned to a role')}
                />
                <FormControlLabel
                  value='COLLEAGUE'
                  control={<Radio />}
                  label={t('adhoc.assignmentByColleague', 'By Colleague - One task assigned to a specific person')}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {assignmentType === 'LOCATION' && (
            <>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  label={t('adhoc.fieldStores', 'Stores')}
                  fullWidth
                  required
                  SelectProps={{
                    multiple: true,
                    value: selectedStoreNumbers,
                    onChange: (e) => setSelectedStoreNumbers(e.target.value as string[]),
                  }}
                  error={!!errors.storeNumbers}
                  helperText={errors.storeNumbers || t('adhoc.helperSelectOneOrMoreStores', 'Select one or more stores')}
                >
                  {filteredStores.map(s => (
                    <MenuItem
                      key={s.storeNumber}
                      value={s.storeNumber}
                    >
                      {s.storeNumber} - {s.storeName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  label={t('adhoc.fieldAssignedRole', 'Assigned Role')}
                  fullWidth
                  value={selectedPersonaId}
                  onChange={e => setSelectedPersonaId(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <MenuItem value=''>{t('adhoc.placeholderSelectRole', '-- Select Role --')}</MenuItem>
                  {fakePersonas.map(p => (
                    <MenuItem
                      key={p.id}
                      value={p.id}
                    >
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}

          {assignmentType === 'COLLEAGUE' && (
            <>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  label={t('adhoc.fieldAssignToRole', 'Assign to Role')}
                  fullWidth
                  value={selectedPersonaId}
                  onChange={e => {
                    setSelectedPersonaId(e.target.value === '' ? '' : Number(e.target.value));
                    if (e.target.value !== '') setSelectedUserId('');
                  }}
                  error={!!errors.assignment}
                  helperText={errors.assignment}
                >
                  <MenuItem value=''>{t('adhoc.placeholderSelectRole', '-- Select Role --')}</MenuItem>
                  {fakePersonas.map(p => (
                    <MenuItem
                      key={p.id}
                      value={p.id}
                    >
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  label={t('adhoc.fieldOrAssignToUser', 'Or Assign to User')}
                  fullWidth
                  value={selectedUserId}
                  onChange={e => {
                    setSelectedUserId(e.target.value === '' ? '' : Number(e.target.value));
                    if (e.target.value !== '') setSelectedPersonaId('');
                  }}
                >
                  <MenuItem value=''>{t('adhoc.placeholderSelectUser', '-- Select User --')}</MenuItem>
                  {fakeUsers.map(u => (
                    <MenuItem
                      key={u.id}
                      value={u.id}
                    >
                      {u.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}
        </Grid>
      </Section>

      {/* Approver Section */}
      {formValues.requiresApproval && (
        <Section style={{ marginTop: '8px', padding: 8, paddingTop: 2 }}>
          <h3>{t('adhoc.approverHeading', 'Approver')}</h3>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              xs={12}
            >
              <FormControl component='fieldset'>
                <FormLabel component='legend'>{t('adhoc.approverQuestion', 'Who should approve completed tasks?')}</FormLabel>
                <RadioGroup
                  row
                  value={approverType}
                  onChange={e => {
                    setApproverType(e.target.value as 'persona' | 'user' | '');
                    setApproverPersonaId('');
                    setApproverUserId('');
                  }}
                >
                  <FormControlLabel
                    value='persona'
                    control={<Radio />}
                    label={t('adhoc.approverRoleLabel', 'Role')}
                  />
                  <FormControlLabel
                    value='user'
                    control={<Radio />}
                    label={t('adhoc.approverSpecificUserLabel', 'Specific User')}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {approverType === 'persona' && (
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  label={t('adhoc.fieldApproverRole', 'Approver Role')}
                  fullWidth
                  required
                  value={approverPersonaId}
                  onChange={e => setApproverPersonaId(e.target.value === '' ? '' : Number(e.target.value))}
                  error={!!errors.approver}
                  helperText={errors.approver}
                >
                  <MenuItem value=''>{t('adhoc.placeholderSelectRole', '-- Select Role --')}</MenuItem>
                  {fakePersonas.map(p => (
                    <MenuItem
                      key={p.id}
                      value={p.id}
                    >
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {approverType === 'user' && (
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  label={t('adhoc.fieldApproverUser', 'Approver User')}
                  fullWidth
                  required
                  value={approverUserId}
                  onChange={e => setApproverUserId(e.target.value === '' ? '' : Number(e.target.value))}
                  error={!!errors.approver}
                  helperText={errors.approver}
                >
                  <MenuItem value=''>{t('adhoc.placeholderSelectUser', '-- Select User --')}</MenuItem>
                  {fakeUsers.map(u => (
                    <MenuItem
                      key={u.id}
                      value={u.id}
                    >
                      {u.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
        </Section>
      )}


      {/* Step Modal */}
      <Dialog
        open={isStepModalOpen}
        onClose={() => setIsStepModalOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>{editingStep ? t('adhoc.editStepTitle', 'Edit Step') : t('adhoc.addStepTitle', 'Add Step')}</DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
          >
            <Grid
              item
              xs={12}
            >
              <FormControl component='fieldset'>
                <FormLabel component='legend'>{t('adhoc.stepTypeLabel', 'Step Type')}</FormLabel>
                <RadioGroup
                  row
                  value={stepFormValues.stepType}
                  onChange={e => setStepFormValues(prev => ({ ...prev, stepType: e.target.value as StepType }))}
                >
                  <FormControlLabel
                    value='text'
                    control={<Radio />}
                    label={t('adhoc.stepTypeText', 'Text Instruction')}
                  />
                  <FormControlLabel
                    value='document'
                    control={<Radio />}
                    label={t('adhoc.stepTypeDocument', 'Link to Document')}
                  />
                  <FormControlLabel
                    value='url'
                    control={<Radio />}
                    label={t('adhoc.stepTypeUrl', 'External URL')}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {stepFormValues.stepType === 'text' && (
              <Grid
                item
                xs={12}
              >
                <TextField
                  label={t('adhoc.fieldInstruction', 'Instruction')}
                  fullWidth
                  required
                  multiline
                  rows={3}
                  value={stepFormValues.instruction}
                  onChange={e => setStepFormValues(prev => ({ ...prev, instruction: e.target.value }))}
                />
              </Grid>
            )}

            {stepFormValues.stepType === 'document' && (
              <Grid
                item
                xs={12}
              >
                <DocumentLinker
                  linkedDocumentIds={stepFormValues.linkedDocumentIds}
                  onChange={(ids) => setStepFormValues(prev => ({ ...prev, linkedDocumentIds: ids }))}
                  franchiseId={selectedFranchiseId ? Number(selectedFranchiseId) : undefined}
                  label={t('adhoc.selectDocumentLabel', 'Select Document')}
                />
                <TextField
                  label={t('adhoc.additionalInstructionsOptional', 'Additional Instructions (optional)')}
                  fullWidth
                  multiline
                  rows={2}
                  value={stepFormValues.instruction}
                  onChange={e => setStepFormValues(prev => ({ ...prev, instruction: e.target.value }))}
                  sx={{ mt: 2 }}
                />
              </Grid>
            )}

            {stepFormValues.stepType === 'url' && (
              <>
                <Grid
                  item
                  xs={12}
                  md={8}
                >
                  <TextField
                    label={t('adhoc.fieldUrl', 'URL')}
                    fullWidth
                    required
                    value={stepFormValues.linkedUrl}
                    onChange={e => setStepFormValues(prev => ({ ...prev, linkedUrl: e.target.value }))}
                    placeholder={t('adhoc.urlPlaceholder', 'https://...')}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <TextField
                    label={t('adhoc.fieldLinkLabel', 'Link Label')}
                    fullWidth
                    value={stepFormValues.linkedUrlLabel}
                    onChange={e => setStepFormValues(prev => ({ ...prev, linkedUrlLabel: e.target.value }))}
                    placeholder={t('adhoc.linkLabelPlaceholder', 'View Guide')}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                >
                  <TextField
                    label={t('adhoc.additionalInstructionsOptional', 'Additional Instructions (optional)')}
                    fullWidth
                    multiline
                    rows={2}
                    value={stepFormValues.instruction}
                    onChange={e => setStepFormValues(prev => ({ ...prev, instruction: e.target.value }))}
                  />
                </Grid>
              </>
            )}

            <Grid
              item
              xs={12}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stepFormValues.evidenceRequired}
                    onChange={e => setStepFormValues(prev => ({
                      ...prev,
                      evidenceRequired: e.target.checked,
                      evidenceType: e.target.checked ? prev.evidenceType : '',
                    }))}
                  />
                }
                label={t('adhoc.evidenceRequiredLabel', 'Evidence required for this step')}
              />
            </Grid>

            {stepFormValues.evidenceRequired && (
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  label={t('adhoc.fieldEvidenceType', 'Evidence Type')}
                  fullWidth
                  required
                  value={stepFormValues.evidenceType}
                  onChange={e => setStepFormValues(prev => ({ ...prev, evidenceType: e.target.value as EvidenceType }))}
                >
                  <MenuItem value=''>{t('adhoc.placeholderSelectType', '-- Select Type --')}</MenuItem>
                  {evidenceTypeOptions.map(opt => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant='secondary'
            onClick={() => setIsStepModalOpen(false)}
          >
            {t('adhoc.cancel', 'Cancel')}
          </Button>
          <Button
            variant='primary'
            onClick={handleSaveStep}
          >
            {editingStep ? t('adhoc.updateStep', 'Update Step') : t('adhoc.addStepTitle', 'Add Step')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Step Confirmation Dialog */}
      <Dialog
        open={isDeleteStepDialogOpen}
        onClose={() => setIsDeleteStepDialogOpen(false)}
      >
        <DialogTitle>{t('adhoc.deleteStepTitle', 'Delete Step')}</DialogTitle>
        <DialogContent>
          <Typography>{t('adhoc.deleteStepConfirm', 'Are you sure you want to delete this step?')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant='secondary'
            onClick={() => setIsDeleteStepDialogOpen(false)}
          >
            {t('adhoc.cancel', 'Cancel')}
          </Button>
          <Button
            variant='primary'
            onClick={handleConfirmDeleteStep}
          >
            {t('adhoc.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default CreateAdhocTask;
